/* SPEC-462 — the bottom status bar. SYSTEM state only.
 *
 * Three things, left to right: which environment this is, ONE health dot for the whole
 * platform, and when the portal last synced. Nothing else belongs here.
 *
 *   - NO DATA DATES. "Data as of …" belongs to each page's DataFreshnessRibbon (SPEC-453):
 *     two dates on one screen make a reader doubt both. The sync time below is when the
 *     SYSTEM last pulled from the portal, which is not any page's data date.
 *   - ONE dot, from `GET /api/observability/health` (SPEC-448 rollup, worst state). It is
 *     never green by default: no answer, a failed call, or an unrecognised state all render
 *     grey with the words "Health unknown" / "Not fully measured" (`config/shell`).
 *     Clicking it opens the pipeline map on /governance, where the reasons live.
 *   - Build sha: OMITTED. There is no version endpoint, and the handoff ruled "omit rather
 *     than invent".
 *   - The sync time comes from `/api/governance/freshness` (the same read the old top-bar
 *     sync chip used), because the rollup does not carry it. It is display metadata, not a
 *     staleness verdict, so it carries NO colour of its own — the dot is the verdict. */
import { Link } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { fetchObservabilityHealth } from '@/lib/api';
import { meta } from '../governance/healthMeta';
import {
  environmentFor,
  ENVIRONMENT_LABEL,
  healthFromRollup,
  healthLabel,
} from '@/config/shell';

const ENV_CLASS = {
  // Staging is LOUD on purpose: nobody should mistake it for the live system.
  staging: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  production: 'bg-[var(--bg-raised)] text-[var(--text-secondary)] border-[var(--border-default)]',
  local: 'bg-[var(--bg-raised)] text-[var(--text-tertiary)] border-[var(--border-default)]',
} as const;

interface StatusBarProps {
  /** ISO timestamp of the last portal sync, or null when unknown. */
  lastSync: string | null;
  /** Humanised age of `lastSync` ("2h ago"). */
  syncAge: string;
  /** 'error' when the freshness read failed — the last sync time may be out of date. */
  syncStatus: 'ok' | 'error' | 'unknown';
}

export function StatusBar({ lastSync, syncAge, syncStatus }: StatusBarProps) {
  const env = environmentFor(typeof window !== 'undefined' ? window.location.hostname : '');
  // 5 minutes, the same cadence as the freshness read. A failed refresh must not leave the
  // LAST good answer on screen as if it were current, so an error reads as unreachable.
  const { data, error, loading } = useApi(fetchObservabilityHealth, [], { refreshInterval: 300_000 });
  const health = healthFromRollup(error ? null : data);
  // First load: say we are checking, not "unknown" — an unanswered first call is not an
  // outage. Still grey, never green.
  const checking = loading && !error && !data;
  const label = checking ? 'Checking health…' : healthLabel(health);
  const dot = health.reachable ? meta(health.state).dot : meta('unknown').dot;
  const tip = [
    `Platform health: ${label}`,
    ...health.reasons,
    'Open the data-flow map for the detail',
  ].join('\n');

  return (
    <footer
      aria-label="System status"
      className="flex items-center gap-4 h-7 shrink-0 px-3 border-t border-[var(--border-subtle)] bg-[var(--sidebar-bg)] text-[11px] text-[var(--text-tertiary)]"
    >
      <span
        className={`px-1.5 py-px rounded border text-[10px] font-semibold uppercase tracking-wider ${ENV_CLASS[env]}`}
        title={`You are on the ${ENVIRONMENT_LABEL[env].toLowerCase()} system`}
      >
        {ENVIRONMENT_LABEL[env]}
      </span>

      <Link
        to="/governance"
        title={tip}
        aria-label={`Platform health: ${label}. Open the data-flow map.`}
        className="flex items-center gap-1.5 rounded px-1 -mx-1 hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors"
      >
        <span className={`w-2 h-2 rounded-full ${dot}`} aria-hidden="true" />
        <span>{label}</span>
      </Link>

      <span title={lastSync ? `Last portal sync: ${lastSync.replace('T', ' ').slice(0, 16)}` : undefined}>
        {syncStatus === 'error'
          // `useFreshness` reports 'error' for BOTH a failed freshness read and a failed
          // pipeline run, so the words fit both: there is a problem, and the time shown is
          // the last one KNOWN, not a claim that it is current.
          ? (lastSync ? `Portal sync problem · last known ${syncAge}` : 'Portal sync status unreadable')
          : lastSync ? `Portal synced ${syncAge}` : 'Portal sync time unknown'}
      </span>
    </footer>
  );
}
